import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
  Stack
} from '@mui/material';
import {
  Business as BusinessIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountBalance as BankIcon,
  Settings as SettingsIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import api from '../../config/axios';
import useModernAlert from '../../hooks/useModernAlert';

interface BusinessUnit {
  id: number;
  name: string;
  ico: string | null;
  dic: string | null;
  address: string | null;
  contactPerson: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  aboSettings?: ABOSettings;
}

interface ABOSettings {
  id: number;
  organizationName: string;
  clientNumber: string;
  fileNumberStart: string;
  fileNumberEnd: string;
  defaultBankAccount: string | null;
  defaultBankCode: string;
  ppbCode: string;
  constantSymbol: string | null;
  specificSymbol: string | null;
  messagePrefix: string;
}

interface BankAccount {
  id: number;
  accountName: string;
  accountNumber: string;
  bankCode: string;
  fioToken: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const BusinessUnits: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showError, showSuccess, showConfirm, AlertComponent } = useModernAlert();

  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBusinessUnit, setEditingBusinessUnit] = useState<BusinessUnit | null>(null);
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState<BusinessUnit | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showABOSettings, setShowABOSettings] = useState(false);
  const [showBankAccounts, setShowBankAccounts] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    ico: '',
    dic: '',
    address: '',
    contactPerson: ''
  });

  const [aboSettings, setABOSettings] = useState({
    organizationName: 'Invoice Factory',
    clientNumber: '0000000000',
    fileNumberStart: '001',
    fileNumberEnd: '999',
    defaultBankAccount: '',
    defaultBankCode: '2010',
    ppbCode: '000',
    constantSymbol: '',
    specificSymbol: '',
    messagePrefix: 'Faktura'
  });

  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [showBankForm, setShowBankForm] = useState(false);
  const [editingBankAccount, setEditingBankAccount] = useState<BankAccount | null>(null);
  const [bankFormData, setBankFormData] = useState({
    accountName: '',
    accountNumber: '',
    bankCode: '',
    token: '',
    isActive: true
  });

  // Fetch business units on component mount
  useEffect(() => {
    fetchBusinessUnits();
  }, []);

  const fetchBusinessUnits = async () => {
    try {
      setLoading(true);
      const response = await api.get('/business-units');
      setBusinessUnits(response.data.businessUnits || []);
    } catch (error) {
      showError('Chyba při načítání firem');
    } finally {
      setLoading(false);
    }
  };

  const fetchBankAccounts = async (businessUnitId: number) => {
    try {
      const response = await api.get(`/business-units/${businessUnitId}/bank-accounts`);
      setBankAccounts(response.data.bankAccounts || []);
    } catch (error) {
      showError('Chyba při načítání bankovních účtů');
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingBusinessUnit) {
        await api.put(`/business-units/${editingBusinessUnit.id}`, formData);
        showSuccess('Účetní jednotka byla úspěšně aktualizována');
      } else {
        await api.post('/business-units', formData);
        showSuccess('Účetní jednotka byla úspěšně vytvořena');
      }
      
      setShowForm(false);
      setEditingBusinessUnit(null);
      resetForm();
      fetchBusinessUnits();
    } catch (error) {
      showError('Chyba při ukládání firmy');
    }
  };

  const handleDelete = async (businessUnit: BusinessUnit) => {
    showConfirm({
      title: '🗑️ Smazat firmu',
      message: `Opravdu chcete smazat firmu "${businessUnit.name}"? Tato akce nelze vrátit zpět.`,
      type: 'warning',
      confirmText: 'Smazat',
      onConfirm: async () => {
        try {
          await api.delete(`/business-units/${businessUnit.id}`);
          showSuccess('Účetní jednotka byla úspěšně smazána');
          fetchBusinessUnits();
        } catch (error) {
          showError('Chyba při mazání firmy');
        }
      }
    });
  };

  const handleEdit = (businessUnit: BusinessUnit) => {
    setEditingBusinessUnit(businessUnit);
    setFormData({
      name: businessUnit.name,
      ico: businessUnit.ico || '',
      dic: businessUnit.dic || '',
      address: businessUnit.address || '',
      contactPerson: businessUnit.contactPerson || ''
    });
    setShowForm(true);
  };

  const handleViewDetails = async (businessUnit: BusinessUnit) => {
    try {
      const response = await api.get(`/business-units/${businessUnit.id}`);
      setSelectedBusinessUnit(response.data.businessUnit);
      setShowDetails(true);
      
      // Load ABO settings
      if (response.data.businessUnit.aboSettings) {
        setABOSettings(response.data.businessUnit.aboSettings);
      }
      
      // Load bank accounts
      await fetchBankAccounts(businessUnit.id);
    } catch (error) {
      showError('Chyba při načítání detailů firmy');
    }
  };

  const handleSaveABOSettings = async () => {
    if (!selectedBusinessUnit) return;
    
    try {
      await api.put(`/business-units/${selectedBusinessUnit.id}/abo-settings`, aboSettings);
      showSuccess('ABO nastavení bylo úspěšně uloženo');
      setShowABOSettings(false);
    } catch (error) {
      showError('Chyba při ukládání ABO nastavení');
    }
  };

  const handleSaveBankAccount = async () => {
    if (!selectedBusinessUnit) return;
    
    try {
      if (editingBankAccount) {
        await api.put(`/business-units/${selectedBusinessUnit.id}/bank-accounts/${editingBankAccount.id}`, bankFormData);
        showSuccess('Bankovní účet byl úspěšně aktualizován');
      } else {
        await api.post(`/business-units/${selectedBusinessUnit.id}/bank-accounts`, bankFormData);
        showSuccess('Bankovní účet byl úspěšně přidán');
      }
      
      setShowBankForm(false);
      setEditingBankAccount(null);
      resetBankForm();
      await fetchBankAccounts(selectedBusinessUnit.id);
    } catch (error) {
      showError('Chyba při ukládání bankovního účtu');
    }
  };

  const handleDeleteBankAccount = async (accountId: number) => {
    if (!selectedBusinessUnit) return;
    
    showConfirm({
      title: '🗑️ Smazat bankovní účet',
      message: 'Opravdu chcete smazat tento bankovní účet? Tato akce nelze vrátit zpět.',
      type: 'warning',
      confirmText: 'Smazat',
      onConfirm: async () => {
        try {
          await api.delete(`/business-units/${selectedBusinessUnit.id}/bank-accounts/${accountId}`);
          showSuccess('Bankovní účet byl úspěšně smazán');
          await fetchBankAccounts(selectedBusinessUnit.id);
        } catch (error) {
          showError('Chyba při mazání bankovního účtu');
        }
      }
    });
  };

  const handleEditBankAccount = (account: BankAccount) => {
    setEditingBankAccount(account);
    setBankFormData({
      accountName: account.accountName,
      accountNumber: account.accountNumber,
      bankCode: account.bankCode,
      token: account.fioToken || '',
      isActive: account.isActive
    });
    setShowBankForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      ico: '',
      dic: '',
      address: '',
      contactPerson: ''
    });
  };

  const resetBankForm = () => {
    setBankFormData({
      accountName: '',
      accountNumber: '',
      bankCode: '',
      token: '',
      isActive: true
    });
  };

  const openForm = () => {
    setShowForm(true);
    setEditingBusinessUnit(null);
    resetForm();
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingBusinessUnit(null);
    resetForm();
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Načítání...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          component="h1"
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
        >
          Správa účetních jednotek
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openForm}
        >
          Přidat účetní jednotku
        </Button>
      </Box>

      {businessUnits.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Zatím nemáte žádné účetní jednotky
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Přidejte první účetní jednotku pro správu bankovních účtů a ABO nastavení
          </Typography>
          <Button variant="contained" onClick={openForm}>
            Přidat první účetní jednotku
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {businessUnits.map((businessUnit) => (
            <Grid item xs={12} md={6} lg={4} key={businessUnit.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {businessUnit.name}
                      </Typography>
                      {businessUnit.ico && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          IČO: {businessUnit.ico}
                        </Typography>
                      )}
                      {businessUnit.dic && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          DIČ: {businessUnit.dic}
                        </Typography>
                      )}
                      {businessUnit.contactPerson && (
                        <Typography variant="body2" color="text.secondary">
                          Kontakt: {businessUnit.contactPerson}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={businessUnit.isActive ? 'Aktivní' : 'Neaktivní'}
                      color={businessUnit.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleViewDetails(businessUnit)}
                      startIcon={<SettingsIcon />}
                    >
                      Detail
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleEdit(businessUnit)}
                      startIcon={<EditIcon />}
                    >
                      Upravit
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => handleDelete(businessUnit)}
                      startIcon={<DeleteIcon />}
                    >
                      Smazat
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Business Unit Form Dialog */}
      <Dialog open={showForm} onClose={closeForm} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingBusinessUnit ? 'Upravit firmu' : 'Přidat novou firmu'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Název firmy *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="IČO"
                value={formData.ico}
                onChange={(e) => setFormData({ ...formData, ico: e.target.value })}
                placeholder="12345678"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="DIČ"
                value={formData.dic}
                onChange={(e) => setFormData({ ...formData, dic: e.target.value })}
                placeholder="CZ12345678"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Adresa"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Kontaktní osoba"
                value={formData.contactPerson}
                onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm}>Zrušit</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!formData.name.trim()}
          >
            {editingBusinessUnit ? 'Uložit' : 'Vytvořit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Business Unit Details Dialog */}
      <Dialog 
        open={showDetails} 
        onClose={() => setShowDetails(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {selectedBusinessUnit?.name}
            </Typography>
            <IconButton onClick={() => setShowDetails(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedBusinessUnit && (
            <Grid container spacing={3}>
              {/* Company Information */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Informace o firmě
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Název:</Typography>
                      <Typography variant="body1">{selectedBusinessUnit.name}</Typography>
                    </Grid>
                    {selectedBusinessUnit.ico && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">IČO:</Typography>
                        <Typography variant="body1">{selectedBusinessUnit.ico}</Typography>
                      </Grid>
                    )}
                    {selectedBusinessUnit.dic && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">DIČ:</Typography>
                        <Typography variant="body1">{selectedBusinessUnit.dic}</Typography>
                      </Grid>
                    )}
                    {selectedBusinessUnit.address && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Adresa:</Typography>
                        <Typography variant="body1">{selectedBusinessUnit.address}</Typography>
                      </Grid>
                    )}
                    {selectedBusinessUnit.contactPerson && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Kontaktní osoba:</Typography>
                        <Typography variant="body1">{selectedBusinessUnit.contactPerson}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>

              {/* ABO Settings */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      ABO nastavení
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setShowABOSettings(true)}
                    >
                      Upravit
                    </Button>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Organizace:</Typography>
                      <Typography variant="body1">{aboSettings.organizationName}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Kód banky:</Typography>
                      <Typography variant="body1">{aboSettings.defaultBankCode}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Bank Accounts */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      Bankovní účty
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setEditingBankAccount(null);
                        resetBankForm();
                        setShowBankForm(true);
                      }}
                    >
                      Přidat účet
                    </Button>
                  </Box>
                  
                  {bankAccounts.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      Zatím nemáte žádné bankovní účty
                    </Typography>
                  ) : (
                    <Grid container spacing={2}>
                      {bankAccounts.map((account) => (
                        <Grid item xs={12} sm={6} key={account.id}>
                          <Card variant="outlined">
                            <CardContent>
                              <Typography variant="subtitle2" gutterBottom>
                                {account.accountName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                {account.accountNumber}/{account.bankCode}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleEditBankAccount(account)}
                                >
                                  <EditIcon fontSize="small" />
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleDeleteBankAccount(account.id)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </Button>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      {/* ABO Settings Dialog */}
      <Dialog open={showABOSettings} onClose={() => setShowABOSettings(false)} maxWidth="md" fullWidth>
        <DialogTitle>ABO nastavení - {selectedBusinessUnit?.name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Název organizace"
                value={aboSettings.organizationName}
                onChange={(e) => setABOSettings({ ...aboSettings, organizationName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Číslo klienta"
                value={aboSettings.clientNumber}
                onChange={(e) => setABOSettings({ ...aboSettings, clientNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Počáteční číslo souboru"
                value={aboSettings.fileNumberStart}
                onChange={(e) => setABOSettings({ ...aboSettings, fileNumberStart: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Koncové číslo souboru"
                value={aboSettings.fileNumberEnd}
                onChange={(e) => setABOSettings({ ...aboSettings, fileNumberEnd: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Výchozí bankovní účet"
                value={aboSettings.defaultBankAccount}
                onChange={(e) => setABOSettings({ ...aboSettings, defaultBankAccount: e.target.value })}
                placeholder="123456789/0800"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Kód banky"
                value={aboSettings.defaultBankCode}
                onChange={(e) => setABOSettings({ ...aboSettings, defaultBankCode: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PPB kód"
                value={aboSettings.ppbCode}
                onChange={(e) => setABOSettings({ ...aboSettings, ppbCode: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Konstantní symbol"
                value={aboSettings.constantSymbol}
                onChange={(e) => setABOSettings({ ...aboSettings, constantSymbol: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Specifický symbol"
                value={aboSettings.specificSymbol}
                onChange={(e) => setABOSettings({ ...aboSettings, specificSymbol: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Prefix zprávy"
                value={aboSettings.messagePrefix}
                onChange={(e) => setABOSettings({ ...aboSettings, messagePrefix: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowABOSettings(false)}>Zrušit</Button>
          <Button onClick={handleSaveABOSettings} variant="contained">
            Uložit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bank Account Form Dialog */}
      <Dialog open={showBankForm} onClose={() => {
        setShowBankForm(false);
        setEditingBankAccount(null);
        resetBankForm();
      }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingBankAccount ? 'Upravit bankovní účet' : 'Přidat bankovní účet'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Název účtu *"
                value={bankFormData.accountName}
                onChange={(e) => setBankFormData({ ...bankFormData, accountName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Číslo účtu *"
                value={bankFormData.accountNumber}
                onChange={(e) => setBankFormData({ ...bankFormData, accountNumber: e.target.value })}
                required
                placeholder="123456-1234567890"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Kód banky *"
                value={bankFormData.bankCode}
                onChange={(e) => setBankFormData({ ...bankFormData, bankCode: e.target.value })}
                required
                placeholder="2010"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="FIO API Token"
                value={bankFormData.token}
                onChange={(e) => setBankFormData({ ...bankFormData, token: e.target.value })}
                type="password"
                placeholder="Pro FIO banku"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowBankForm(false);
            setEditingBankAccount(null);
            resetBankForm();
          }}>Zrušit</Button>
          <Button 
            onClick={handleSaveBankAccount} 
            variant="contained"
            disabled={!bankFormData.accountName || !bankFormData.accountNumber || !bankFormData.bankCode}
          >
            {editingBankAccount ? 'Uložit' : 'Přidat'}
          </Button>
        </DialogActions>
      </Dialog>

      {AlertComponent}
    </Box>
  );
};

export default BusinessUnits;
